import clsx from 'clsx';
import { PaymentViewLayout } from 'components/Layout/PaymentViewLayout';
import React from 'react';

import { Badge } from '@fragment/ui/src/components/Badge/Badge';
import { kLabelColumnWidth } from '@fragment/ui/src/components/Payments/LayoutRow';

import { DynamicInvoiceIcon } from './DynamicInvoiceIcon';

/**
 * Skeleton for invoice detail: same div structure as loaded state (header, title row, issued, total, label row, users with items, payins/payouts). No text.
 */
const noop = (): void => {};

export const InvoiceDetailLoading = (): JSX.Element => (
  <PaymentViewLayout
    title=""
    onBack={noop}
    backTitle=""
    secondaryAction={{ title: 'History', onClick: noop, disabled: true }}
    primaryAction={{ title: 'Adjust', onClick: noop, disabled: true }}
  >
    <div className="text-xmd font-helvetica flex flex-row w-full items-start">
      <div className="flex flex-col flex-1 min-w-0">
        {/* Title row */}
        <div className="flex w-full items-start border-b-[0.5px] border-main-200 h-f12">
          <div className="flex items-start gap-x-f1 px-[13.5px] flex-1 min-w-0 h-full">
            <Badge
              badge={<DynamicInvoiceIcon colors={['#D9D9D9']} height={24} />}
            >
              <span className="text-lg invisible">Loading</span>
            </Badge>
          </div>
          <div className="flex flex-col h-full items-end justify-start p-[13.5px] shrink-0 border-l-[0.5px] border-main-200">
            <div className="w-[60px] h-[24px] rounded-[4px] border-[0.5px] border-main-300 shrink-0" />
          </div>
        </div>

        {/* Issued row */}
        <div className="flex items-center border-b-[0.5px] border-main-200 w-full h-f4">
          <div
            className={clsx(
              'px-[13.5px] border-r-[0.5px] border-main-200 shrink-0 h-full flex items-center',
              kLabelColumnWidth
            )}
          />
          <div className="px-[13.5px] flex-1 font-helvetica h-full" />
        </div>

        {/* Total row */}
        <div className="flex items-start h-f8 w-full border-b-[0.5px] border-main-300">
          <div
            className={clsx(
              'px-[13.5px] pt-[13.5px] border-r-[0.5px] border-main-200 shrink-0 h-full',
              kLabelColumnWidth
            )}
          />
          <div className="px-[13.5px] pt-[13.5px] flex-1 min-h-[2lh]" />
        </div>

        {/* Tab row */}
        <div className="flex items-end w-full">
          <div className="px-[13.5px] py-f1 text-xmd text-main-500 border-main-200 border-l-[0.5px]">
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          </div>
          <div className="px-[13.5px] py-f1 text-xmd text-main-500 border-b-[0.5px] border-l-[0.5px] border-main-200">
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          </div>
          <div className="flex-1 border-b-[0.5px] border-main-200" />
        </div>

        {/* Label row (Users | Name | Qty | Price | Due) */}
        <div className="flex flex-row w-full items-center border-b-[0.5px] border-main-200 h-f4">
          <span
            className={clsx(
              kLabelColumnWidth,
              'shrink-0 text-main-500 text-xmd px-[13.5px] border-r-[0.5px] border-main-200'
            )}
          />
          <div className="flex flex-row flex-1 justify-between min-w-0">
            <span className="px-[13.5px]" />
            <span className="px-[13.5px]" />
          </div>
        </div>

        {/* User 1 */}
        <div className="flex flex-row w-full border-b-[0.5px] border-main-200">
          <div
            className={clsx(
              kLabelColumnWidth,
              'shrink-0 px-[13.5px] border-r-[0.5px] border-main-200 flex items-start min-w-0'
            )}
          >
            <div className="flex items-center gap-x-f1 h-[2lh]">
              <div className="w-[14px] h-[14px] rounded-full shrink-0 bg-main-200" />
              <span className="flex-1 min-w-0" />
            </div>
          </div>
          <div className="flex flex-col flex-1 min-w-0">
            <div className="flex flex-row justify-end items-center gap-x-f1 h-f4 px-[13.5px] border-b-[0.5px] border-main-200" />
            <div
              className={clsx(
                'flex flex-row w-full items-stretch border-main-200 border-b-[0.5px]'
              )}
            >
              <div className="flex items-center justify-center w-[2lh] min-w-[2lh] shrink-0 border-r-[0.5px] border-main-200 aspect-square" />
              <div className="flex items-center justify-center w-[2lh] min-w-[2lh] shrink-0 border-r-[0.5px] border-main-200 aspect-square" />
              <div className="flex items-center gap-x-f1 px-[13.5px] min-w-0 overflow-hidden flex-1 basis-0 flex-[3]">
                <div className="w-[14px] h-[14px] shrink-0 rounded-[2px] bg-main-200" />
                <span className="flex-1 min-w-0" />
              </div>
              <div className="flex items-center px-[13.5px] min-w-0 flex-1 basis-0 flex-[0.5]" />
              <div className="flex items-center px-[13.5px] min-w-0 flex-1 basis-0" />
              <div className="flex flex-row justify-end items-center gap-x-f1 px-[13.5px] min-w-0 flex-1 basis-0" />
            </div>
            <div
              className={clsx(
                'flex flex-row w-full items-stretch border-main-200'
              )}
            >
              <div className="flex items-center justify-center w-[2lh] min-w-[2lh] shrink-0 border-r-[0.5px] border-main-200 aspect-square" />
              <div className="flex items-center justify-center w-[2lh] min-w-[2lh] shrink-0 border-r-[0.5px] border-main-200 aspect-square" />
              <div className="flex items-center gap-x-f1 px-[13.5px] min-w-0 overflow-hidden flex-1 basis-0 flex-[3]">
                <div className="w-[14px] h-[14px] shrink-0 rounded-[2px] bg-main-200" />
                <span className="flex-1 min-w-0" />
              </div>
              <div className="flex items-center px-[13.5px] min-w-0 flex-1 basis-0 flex-[0.5]" />
              <div className="flex items-center px-[13.5px] min-w-0 flex-1 basis-0" />
              <div className="flex flex-row justify-end items-center gap-x-f1 px-[13.5px] min-w-0 flex-1 basis-0" />
            </div>
          </div>
        </div>

        {/* User 2 */}
        <div className="flex flex-row w-full border-b-[0.5px] border-main-200">
          <div
            className={clsx(
              kLabelColumnWidth,
              'shrink-0 px-[13.5px] border-r-[0.5px] border-main-200 flex items-start min-w-0'
            )}
          >
            <div className="flex items-center gap-x-f1 h-[2lh]">
              <div className="w-[14px] h-[14px] rounded-full shrink-0 bg-main-200" />
              <span className="flex-1 min-w-0" />
            </div>
          </div>
          <div className="flex flex-col flex-1 min-w-0">
            <div className="flex flex-row justify-end items-center gap-x-f1 h-f4 px-[13.5px] border-b-[0.5px] border-main-200" />
            <div
              className={clsx(
                'flex flex-row w-full items-stretch border-main-200 border-b-[0.5px]'
              )}
            >
              <div className="flex items-center justify-center w-[2lh] min-w-[2lh] shrink-0 border-r-[0.5px] border-main-200 aspect-square" />
              <div className="flex items-center justify-center w-[2lh] min-w-[2lh] shrink-0 border-r-[0.5px] border-main-200 aspect-square" />
              <div className="flex items-center gap-x-f1 px-[13.5px] min-w-0 overflow-hidden flex-1 basis-0 flex-[3]">
                <div className="w-[14px] h-[14px] shrink-0 rounded-[2px] bg-main-200" />
                <span className="flex-1 min-w-0" />
              </div>
              <div className="flex items-center px-[13.5px] min-w-0 flex-1 basis-0 flex-[0.5]" />
              <div className="flex items-center px-[13.5px] min-w-0 flex-1 basis-0" />
              <div className="flex flex-row justify-end items-center gap-x-f1 px-[13.5px] min-w-0 flex-1 basis-0" />
            </div>
          </div>
        </div>

        {/* Totals row */}
        <div className="flex items-center border-b-[0.5px] border-main-200 w-full h-f4 px-[13.5px]" />

        {/* Payins row */}
        <div className="flex items-stretch border-b-[0.5px] border-main-200 w-full min-h-[2lh]">
          <div
            className={clsx(
              kLabelColumnWidth,
              'shrink-0 border-r-[0.5px] border-main-200'
            )}
          />
          <div className="flex items-center justify-center w-[2lh] min-w-[2lh] shrink-0 border-r-[0.5px] border-main-200 aspect-square" />
          <div className="flex items-center justify-center w-[2lh] min-w-[2lh] shrink-0 border-r-[0.5px] border-main-200 aspect-square" />
          <div className="flex flex-row justify-between items-center gap-x-f1 px-[13.5px] flex-1 min-w-0">
            <span className="min-w-0" />
            <span className="flex flex-row justify-end gap-x-f1 min-w-0" />
          </div>
        </div>

        {/* Payouts row */}
        <div className="flex items-stretch border-b-[0.5px] border-main-200 w-full min-h-[2lh]">
          <div
            className={clsx(
              kLabelColumnWidth,
              'shrink-0 border-r-[0.5px] border-main-200'
            )}
          />
          <div className="flex items-center justify-center w-[2lh] min-w-[2lh] shrink-0 border-r-[0.5px] border-main-200 aspect-square" />
          <div className="flex items-center justify-center w-[2lh] min-w-[2lh] shrink-0 border-r-[0.5px] border-main-200 aspect-square" />
          <div className="flex flex-row justify-between items-center gap-x-f1 px-[13.5px] flex-1 min-w-0">
            <span className="min-w-0" />
            <span className="flex flex-row justify-end gap-x-f1 min-w-0" />
          </div>
        </div>
      </div>
    </div>
  </PaymentViewLayout>
);
